import Button from '@aurora/shared-client/components/common/Button/Button';
import { ButtonVariant } from '@aurora/shared-client/components/common/Button/enums';
import { IconSize } from '@aurora/shared-client/components/common/Icon/enums';
import Icon from '@aurora/shared-client/components/common/Icon/Icon';
import TenantContext from '@aurora/shared-client/components/context/TenantContext';
import useAuthFlow from '@aurora/shared-client/components/useAuthFlow';
import useMutationWithTracing from '@aurora/shared-client/components/useMutationWithTracing';
import useRegistrationStatus from '@aurora/shared-client/components/users/useRegistrationStatus';
import Icons from '@aurora/shared-client/icons';
import { AuthFlow } from '@aurora/shared-client/types/enums';
import { EndUserComponent } from '@aurora/shared-types/pages/enums';
import React, { useContext, useState } from 'react';
import { useClassNameMapper } from 'react-bootstrap';
import useGlobalState, { GlobalStateType } from '@aurora/shared-client/helpers/ui/GlobalState';
import { MessageFeedbackDisplayType, MessageFeedbackValue } from '../../../types/enums';
import type {
  CreateHelpfulnessRatingMutation,
  CreateHelpfulnessRatingMutationVariables,
  MessageViewFragment
} from '../../../types/graphql-types';
import useTranslation from '../../useTranslation';
import createHelpfulnessRatingMutation from './CreateHelpfulnessRating.mutation.graphql';
import localStyle from './MessageHelpfulness.module.css';
import EmailVerificationContext from '../../context/EmailVerificationContext/EmailVerificationContext';

interface Props {
  /**
   * The message to display helpfulness for.
   */
  message: MessageViewFragment;
  /**
   * The list of reasons a message is found unhelpful.
   */
  unhelpfulReasons: Array<string>;
  /**
   * Whether to use buttons or emoticons to provide feedback.
   */
  displayType?: MessageFeedbackDisplayType;
}
/**
 * Message Helpfulness Feedback.
 * @author Arjun Krishna
 */
const MessageHelpfulness: React.FC<React.PropsWithChildren<Props>> = ({
  message,
  unhelpfulReasons,
  displayType = MessageFeedbackDisplayType.ICON
}) => {
  const cx = useClassNameMapper(localStyle);
  const { formatMessage, loading: textLoading } = useTranslation(
    EndUserComponent.MESSAGE_HELPFULNESS
  );
  const [, setMessageEditingState] = useGlobalState(GlobalStateType.MESSAGE_EDITING_STATE);
  const { id } = message;
  const { confirmEmailStatus } = useRegistrationStatus();
  const { addUpdateEmailToast } = useContext(EmailVerificationContext);
  const { isAnonymous } = useRegistrationStatus();
  const { triggerAuthFlow } = useAuthFlow();
  const tenant = useContext(TenantContext);
  const {
    publicConfig: { multiAuthEnabled }
  } = tenant;
  const [helpfulFeedback] = useMutationWithTracing<
    CreateHelpfulnessRatingMutation,
    CreateHelpfulnessRatingMutationVariables
  >(module, createHelpfulnessRatingMutation);
  const [feedbackValue, setFeedbackValue] = useState<MessageFeedbackValue>(
    MessageFeedbackValue.NEUTRAL
  );
  const [feedbackCompleted, setFeedbackCompleted] = useState<boolean>(false);

  if (textLoading) {
    return null;
  }

  const feedbackPositiveVariant =
    feedbackValue === MessageFeedbackValue.HELPFUL
      ? ButtonVariant.SUCCESS
      : ButtonVariant.SECONDARY;
  const feedbackNegativeVariant =
    feedbackValue === MessageFeedbackValue.UNHELPFUL
      ? ButtonVariant.WARNING
      : ButtonVariant.SECONDARY;

  /**
   * Assign class names based on Feedback states.
   */

  const feedbackClassVariant =
    feedbackValue === MessageFeedbackValue.HELPFUL
      ? MessageFeedbackValue[MessageFeedbackValue.HELPFUL].toLowerCase()
      : feedbackValue === MessageFeedbackValue.UNHELPFUL
      ? MessageFeedbackValue[MessageFeedbackValue.UNHELPFUL].toLowerCase()
      : MessageFeedbackValue[MessageFeedbackValue.NEUTRAL].toLowerCase();

  /**
   *  Make the call to set the feedback value and text.
   *
   * @param value
   * @param feedback
   */
  async function handleHelpfulFeedback(value: MessageFeedbackValue, feedback = '') {
    if (isAnonymous) {
      await triggerAuthFlow(
        (ssoEnabled: boolean) => {
          if (!ssoEnabled) {
            setMessageEditingState(null);
          }
        },
        multiAuthEnabled ? AuthFlow.MULTI_AUTH_LOGIN : AuthFlow.LOGIN
      );
    } else {
      if (!confirmEmailStatus) {
        addUpdateEmailToast();
      } else {
        setFeedbackValue(value);
        setFeedbackCompleted(
          value === MessageFeedbackValue.HELPFUL ||
            (value === MessageFeedbackValue.UNHELPFUL && feedback !== '')
        );

        const { errors } = await helpfulFeedback({
          variables: {
            messageId: id,
            createInput: {
              value: value > 0,
              feedback
            }
          }
        });
        if (errors?.length > 0) {
          setFeedbackValue(MessageFeedbackValue.NEUTRAL);
        }
      }
    }
  }

  /**
   * To set tabIndex so that the buttons are not accessible after the feedback is given.
   */
  function findTabIndex(): number {
    return feedbackValue === MessageFeedbackValue.NEUTRAL ? 0 : -1;
  }

  /**
   * Render the icon variant for helpfulness feedback.
   *
   */
  function renderHelpfulnessIconVariant(): React.ReactElement {
    return (
      displayType === MessageFeedbackDisplayType.ICON && (
        <div
          className={cx(
            'lia-feedback-icon-container',
            `lia-feedback-icon-variant-${feedbackClassVariant}`
          )}
        >
          <Button
            variant={ButtonVariant.NO_VARIANT}
            className={cx('lia-feedback-icon-wrapper')}
            aria-label="helpful"
            disabled={feedbackValue === MessageFeedbackValue.UNHELPFUL}
            onClick={() => handleHelpfulFeedback(MessageFeedbackValue.HELPFUL)}
            tabIndex={findTabIndex()}
            data-testid="Helpful-Icon"
          >
            <Icon
              icon={Icons.FaceHappyIcon}
              size={IconSize.FROM_CSS}
              className={cx('lia-feedback-icon lia-happy-icon')}
            />
          </Button>
          <Button
            variant={ButtonVariant.NO_VARIANT}
            className={cx('lia-feedback-icon-wrapper')}
            aria-label="unhelpful"
            disabled={feedbackValue === MessageFeedbackValue.HELPFUL}
            onClick={() => handleHelpfulFeedback(MessageFeedbackValue.UNHELPFUL)}
            tabIndex={findTabIndex()}
            data-testid="Unhelpful-Icon"
          >
            <Icon
              icon={Icons.FaceSadIcon}
              size={IconSize.FROM_CSS}
              className={cx('lia-feedback-icon lia-sad-icon')}
            />
          </Button>
        </div>
      )
    );
  }

  /**
   * Render the button variant for helpfulness feedback.
   *
   */
  function renderHelpfulnessButtonVariant(): React.ReactElement {
    return (
      displayType === MessageFeedbackDisplayType.BUTTON && (
        <div
          className={cx(
            'lia-feedback-button-container',
            `lia-feedback-button-variant-${feedbackClassVariant}`
          )}
        >
          <Button
            type="button"
            variant={feedbackPositiveVariant}
            className={cx('lia-feedback-button lia-happy-button')}
            aria-label="helpful"
            onClick={() => handleHelpfulFeedback(MessageFeedbackValue.HELPFUL)}
            disabled={feedbackValue === MessageFeedbackValue.UNHELPFUL}
            tabIndex={findTabIndex()}
            data-testid="Helpful-Button"
          >
            {formatMessage('positiveFeedback')}
          </Button>

          <Button
            type="button"
            variant={feedbackNegativeVariant}
            className={cx('lia-feedback-button lia-sad-button')}
            aria-label="unhelpful"
            onClick={() => handleHelpfulFeedback(MessageFeedbackValue.UNHELPFUL)}
            disabled={feedbackValue === MessageFeedbackValue.HELPFUL}
            tabIndex={findTabIndex()}
            data-testid="Unhelpful-Button"
          >
            {formatMessage('negativeFeedback')}
          </Button>
        </div>
      )
    );
  }

  /**
   * Render the list of reasons for an article being unhelpful.
   *
   */
  function renderFeedback(): React.ReactElement {
    return (
      feedbackValue === MessageFeedbackValue.UNHELPFUL &&
      !feedbackCompleted && (
        <div className={cx('lia-feedback-reason-container')}>
          <p className={cx('lia-helpfulness-text lia-g-mb-20')}>
            {formatMessage('unhelpfulReason')}
          </p>
          {unhelpfulReasons.map(reason => {
            return (
              <Button
                variant={ButtonVariant.SECONDARY}
                className={cx('lia-feedback-reason-button')}
                onClick={() => handleHelpfulFeedback(MessageFeedbackValue.UNHELPFUL, reason)}
                key={reason}
              >
                {reason}
              </Button>
            );
          })}
        </div>
      )
    );
  }

  return (
    <section className={cx('lia-helpfulness-container')} data-testid="MessageHelpfulness">
      <p className={cx('lia-helpfulness-text')}>{formatMessage('helpfulnessQuery')}</p>
      <div className={cx('lia-g-mb-15')}>
        {renderHelpfulnessIconVariant()}
        {renderHelpfulnessButtonVariant()}
      </div>
      {feedbackCompleted && (
        <p className={cx('lia-helpfulness-text lia-feedback-complete-text')}>
          {formatMessage('feedbackCompleteText')}
        </p>
      )}
      {renderFeedback()}
    </section>
  );
};

export default MessageHelpfulness;
